import { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Divider from '@mui/material/Divider'
import InputAdornment from '@mui/material/InputAdornment'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import SearchIcon from '@mui/icons-material/Search'
import SettingsBackupRestoreOutlinedIcon from '@mui/icons-material/SettingsBackupRestoreOutlined'
import type { GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid-premium'
import COLORS from 'lib/colors'
import colors from 'lib/colors'
import { LeadRow } from './leadTableTypes'


type LeadTableColumnsManagementProps = {
  columns: GridColDef<LeadRow>[]
  columnVisibilityModel: GridColumnVisibilityModel
  onColumnVisibilityModelChange: (model: GridColumnVisibilityModel) => void
  onResetColumns: () => void
}

function getColumnLabel(column: GridColDef) {
  return column.headerName || column.field
}

export default function LeadTableColumnsManagement({
  columns,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  onResetColumns,
}: LeadTableColumnsManagementProps) {
  const [search, setSearch] = useState('')

  // Capture the initial column order once. We intentionally never update it
  // so rows don't jump while toggling visibility - order is re-determined
  // when the menu opens.
  const [orderedColumns] = useState<GridColDef[]>(() =>
    columns.filter((c) => c.hideable !== false)
  )

  const filteredColumns = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()

    if (!normalizedSearch) {
      return orderedColumns
    }

    return orderedColumns.filter((column) =>
      getColumnLabel(column).toLowerCase().includes(normalizedSearch)
    )
  }, [orderedColumns, search])

  const visibleFilteredColumns = filteredColumns.filter(
    (column) => columnVisibilityModel[column.field] !== false
  )

  const allFilteredColumnsVisible =
    filteredColumns.length > 0 && visibleFilteredColumns.length === filteredColumns.length

  const someFilteredColumnsVisible =
    visibleFilteredColumns.length > 0 && visibleFilteredColumns.length < filteredColumns.length

  const handleToggleColumn = (field: string) => {
    onColumnVisibilityModelChange({
      ...columnVisibilityModel,
      [field]: columnVisibilityModel[field] === false,
    })
  }

  const handleToggleAll = () => {
    const nextVisible = !allFilteredColumnsVisible

    onColumnVisibilityModelChange({
      ...columnVisibilityModel,
      ...Object.fromEntries(filteredColumns.map((column) => [column.field, nextVisible])),
    })
  }

  return (
    <Box className='MuiDataGrid-columnsManagement' sx={{ width: 288, p: 3 }}>
      <TextField
        autoFocus
        fullWidth
        value={search}
        placeholder='Search'
        onChange={(event) => setSearch(event.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <SearchIcon fontSize='small' />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiInputBase-root': { height: '40px', fontSize: '14px' },
          '& .MuiInputBase-inputAdornedStart': { pl: 0 },
          '& .MuiSvgIcon-root': { color: colors.cadetGray },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.slateGray },
        }}
      />

      <Divider sx={{ mt: 2 }} />

      <Box
        sx={{
          height: 260,
          overflowY: 'auto',
          mx: -3,
          py: 1,
          '& .MuiListItemText-primary': { fontSize: '14px' },
        }}
      >
        <MenuItem
          onClick={handleToggleAll}
          disabled={filteredColumns.length === 0}
          sx={{
            minHeight: 22,
            px: 1,
            py: 0.5,
          }}
        >
          <ListItemIcon>
            <Checkbox
              edge='start'
              checked={allFilteredColumnsVisible}
              indeterminate={someFilteredColumnsVisible}
              tabIndex={-1}
              disableRipple
            />
          </ListItemIcon>

          <ListItemText primary='Show/Hide All' />
        </MenuItem>

        {filteredColumns.map((column) => {
          const checked = columnVisibilityModel[column.field] !== false

          return (
            <MenuItem
              key={column.field}
              onClick={() => handleToggleColumn(column.field)}
              sx={{
                minHeight: 22,
                px: 1,
                py: 0.5,
              }}
            >
              <ListItemIcon>
                <Checkbox edge='start' checked={checked} tabIndex={-1} disableRipple />
              </ListItemIcon>

              <ListItemText primary={getColumnLabel(column)} />
            </MenuItem>
          )
        })}
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Button
        variant='outlined'
        startIcon={<SettingsBackupRestoreOutlinedIcon />}
        onClick={onResetColumns}
        sx={{
          borderColor: COLORS.frenchBlue,
          color: COLORS.frenchBlue,
          textTransform: 'none',
          fontSize: '14px',
          height: '40px',
          px: 3,
        }}
      >
        Reset
      </Button>
    </Box>
  )
}
